import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
  Dimensions,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { AdminTargetsTable } from "../components/Admin";
import { RootState, AppDispatch } from "../../../store";
import {
  fetchAdminTargets,
  saveAdminTargets,
  deleteAdminTarget,
  AdminTarget,
  clearError,
  clearSaveError,
  clearSaveSuccess,
  clearDeleteError,
  clearDeleteSuccess,
} from "../../../store/slices/nhoAdminSlice";
import LHLoader from "../../../components/LHLoader";
import LHConfirmationDialog from "../../../components/LHConfirmationDialog";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../../modules/dlo/components/DLOBreadcrumb";
import { useTheme } from "../../../theme";

// Define props for AdminPage component
interface AdminPageProps {
  onBackPress?: () => void;
  onHomePress?: () => void; // Added for home navigation
}

const isIPad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;

const AdminPage: React.FC<AdminPageProps> = ({ onBackPress, onHomePress }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  
  // Select data from Redux store
  const {
    targets,
    loading,
    error,
    saveLoading,
    saveError,
    saveSuccess,
    deleteLoading,
    deleteError,
    deleteSuccess,
  } = useSelector((state: RootState) => state.nhoAdmin);
  const locationName = useSelector(
    (state: RootState) => state.appData.selectedLocation?.displayText
  );

  // Local state for table data
  const [tableData, setTableData] = useState<AdminTarget[]>([]);

  // State for tracking unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  // Fetch targets on component mount
  useEffect(() => {
    if (locationName) {
      dispatch(fetchAdminTargets(locationName));
    }
  }, [dispatch, locationName]);

  // Update table data when targets change
  useEffect(() => {
    if (targets && targets.length > 0) {
      // Use all available weeks from the API response (showing all rows)
      // Still enforce maximum of 52 rows as before
      const initialWeeks = targets.length > 52 ? targets.slice(0, 52) : targets;

      // Set table data
      setTableData(initialWeeks);
      // Reset unsaved changes flag when initial data is loaded
      setHasUnsavedChanges(false);
      // No need to update weekCounter as we're showing all rows

      console.log(
        "AdminPage: Updated tableData from targets, new length:",
        initialWeeks.length
      );
    }
  }, [targets]);

  // Clear success message after 3 seconds and reset unsaved changes flag
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (saveSuccess) {
      // Clear unsaved changes flag since we've just saved
      setHasUnsavedChanges(false);

      // No API refresh after save - rely on local state
      console.log("AdminPage: Save successful, no API refresh needed");

      timer = setTimeout(() => {
        dispatch(clearSaveSuccess());
      }, 3000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [saveSuccess, dispatch]);

  // Clear delete success message after 3 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (deleteSuccess) {
      // No API refresh after delete - rely on local state
      console.log("AdminPage: Delete successful, no API refresh needed");

      // Schedule clearing of success message
      timer = setTimeout(() => {
        dispatch(clearDeleteSuccess());
      }, 3000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [deleteSuccess, dispatch]);

  // Handle table data changes
  const handleTableDataChange = (newData: AdminTarget[]) => {
    // Ensure we never exceed 52 rows total
    const limitedData = newData.length > 52 ? newData.slice(0, 52) : newData;
    setTableData(limitedData);

    // Only mark as having unsaved changes if the data is actually different from the original
    // Compare with the original fetched data from Redux
    const originalData = targets || [];
    const hasActualChanges =
      JSON.stringify(limitedData) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasActualChanges);
  };

  // Handle retry when API call fails
  const handleRetry = () => {
    if (locationName) {
      dispatch(clearError());
      dispatch(fetchAdminTargets(locationName));
    }
  };

  // Handle save changes when button is clicked
  const handleSaveChanges = () => {
    if (locationName && tableData.length > 0) {
      // Ensure every row has an Id and all required fields are properly set
      const updatesWithIds = tableData.map((row) => {
        // Ensure all values are valid numbers
        const ensureNumber = (value: any) => {
          const num = parseInt(value, 10);
          return isNaN(num) ? 0 : num;
        };

        return {
          ...row,
          Id: row.Id || 0, // Ensure ID is always provided
          Week: row.Week, // Keep week name
          // Ensure all numeric fields are valid numbers
          GAP_Time_Target: ensureNumber(row.GAP_Time_Target),
          DPS_Target: ensureNumber(row.DPS_Target),
          CPH_Target: ensureNumber(row.CPH_Target),
          SIA_Target: ensureNumber(row.SIA_Target),
          Attendance: ensureNumber(row.Attendance),
        };
      });

      // Log detailed information about what we're sending
      console.log("Saving all admin targets - record details:");
      updatesWithIds.forEach((row, index) => {
        console.log(
          `Record ${index + 1}: ID=${row.Id}, Week=${
            row.Week
          }, GAP_Time_Target=${row.GAP_Time_Target}, DPS=${
            row.DPS_Target
          }, CPH=${row.CPH_Target}, SIA=${row.SIA_Target}, Attendance=${
            row.Attendance
          }`
        );
      });

      console.log(
        "Total records being sent for update:",
        updatesWithIds.length
      );

      // Dispatch the save action with ALL records
      // This ensures all records are updated correctly
      dispatch(
        saveAdminTargets({
          Location_Name: locationName,
          updates: updatesWithIds,
        })
      );

      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
    } else {
      console.error(
        "Cannot save: location name is missing or table data is empty"
      );
    }
  };

  // Handle dismissing save error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDismissSaveError = () => {
    dispatch(clearSaveError());
  };

  // Handle deleting a target row - make DELETE API call
  const handleDeleteTarget = (id?: number, locationName?: string) => {
    // Ensure we always have at least 4 rows
    if (tableData.length <= 4) {
      return;
    }

    if (id && locationName) {
      console.log(
        "AdminPage: Making DELETE API call for target with ID:",
        id,
        "and locationName:",
        locationName
      );

      // Dispatch the DELETE action to the Redux store with both id and locationName
      dispatch(deleteAdminTarget({ id, locationName }));
    } else {
      console.log(
        "AdminPage: Missing ID or locationName for delete operation. ID:",
        id,
        "LocationName:",
        locationName
      );
    }
  };

  // Handle dismissing delete error
  const handleDismissDeleteError = () => {
    dispatch(clearDeleteError());
  };

  // Handle back button click
  const handleBackClick = () => {
    // If there are unsaved changes, show confirmation dialog
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      // If no unsaved changes, just go back
      if (onBackPress) {
        onBackPress();
      }
    }
  };

  // Handle confirming navigation - user wants to redirect
  const handleConfirmNavigation = () => {
    setShowConfirmDialog(false);
    // User confirmed they want to leave, navigate away
    if (onBackPress) {
      onBackPress();
    }
  };

  // Handle canceling navigation - user wants to stay
  const handleCancelNavigation = () => {
    setShowConfirmDialog(false);
    // User canceled, stay on the current page
  };

  // Breadcrumb items for Admin Page
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: 'nho',
      label: 'NHO',
      onPress: () => {
        if (hasUnsavedChanges) {
          setShowConfirmDialog(true);
        } else {
          handleBackClick();
        }
      },
    },
    {
      id: 'admin-page',
      label: 'Admin Page',
      isCurrentPage: true,
    },
  ];

  return (
    <ScrollView
      style={[styles.container, isDarkTheme && { backgroundColor: colors.background.page }]}
    >
      <View style={styles.headerContainer}>
        <DLOBreadcrumb 
          items={breadcrumbItems} 
          onHomePress={onHomePress}
          showHomeIcon={true}
        />
      </View>

      <View style={styles.contentContainer}>
        <Text style={[styles.titleText, isDarkTheme && { color: colors.text.primary }]}>Weekly Target Configuration</Text>

        {/* Error Message & Retry Button */}
        {error && (
          <View
            style={[
              styles.errorContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderWidth: 1,
              },
            ]}
          >
            <Text style={[styles.errorText, isDarkTheme && { color: colors.text.primary }]}>
              Failed to load targets: {error}
            </Text>
            <TouchableOpacity
              style={[
                styles.retryButton,
                isDarkTheme && { backgroundColor: colors.brand.primary },
              ]}
              onPress={handleRetry}
              testID="retry-button"
            >
              <Text style={styles.retryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Success Message */}
        {saveSuccess && (
          <View
            style={[
              styles.successContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderWidth: 1,
              },
            ]}
          >
            <Text style={[styles.successText, isDarkTheme && { color: colors.text.primary }]}>Changes saved successfully!</Text>
          </View>
        )}

        {/* Success Message for Delete */}
        {deleteSuccess && (
          <View
            style={[
              styles.successContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderWidth: 1,
              },
            ]}
          >
            <Text style={[styles.successText, isDarkTheme && { color: colors.text.primary }]}>Target deleted successfully!</Text>
          </View>
        )}

        {/* Loading State */}
        {loading || saveLoading ? (
          <View style={styles.loadingContainer}>
            <LHLoader />
          </View>
        ) : (
          <>
            {/* Table */}
            {tableData.length > 0 && (
              <>
                <View style={styles.tableContainer}>
                  <AdminTargetsTable
                    initialData={tableData}
                    onDataChange={handleTableDataChange}
                    onSaveChanges={handleSaveChanges}
                    saveLoading={saveLoading}
                    resetChanges={saveSuccess}
                    onDeleteTarget={handleDeleteTarget}
                    deleteLoading={deleteLoading}
                    deleteSuccess={deleteSuccess} // This will trigger the effect in AdminTargetsTable
                    testID="admin-targets-table"
                  />
                </View>
              </>
            )}
          </>
        )}

        {/* Error Message for Save Failure */}
        {saveError && (
          <View
            style={[
              styles.errorContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderWidth: 1,
              },
            ]}
          >
            <Text style={[styles.errorText, isDarkTheme && { color: colors.text.primary }]}>
              Failed to save changes: {saveError}
            </Text>
            <TouchableOpacity
              style={[
                styles.retryButton,
                isDarkTheme && { backgroundColor: colors.brand.primary },
              ]}
              onPress={handleSaveChanges}
              testID="retry-save-button"
            >
              <Text style={styles.retryText}>Retry Save</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Note: We don't need a separate UI for Add Row error because it uses the same saveError state */}

        {/* Error Message for Delete Failure */}
        {deleteError && (
          <View
            style={[
              styles.errorContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderWidth: 1,
              },
            ]}
          >
            <Text style={[styles.errorText, isDarkTheme && { color: colors.text.primary }]}>
              Failed to delete target: {deleteError}
            </Text>
            <TouchableOpacity
              style={[
                styles.retryButton,
                isDarkTheme && { backgroundColor: colors.brand.primary },
              ]}
              onPress={handleDismissDeleteError} // Can't retry since we don't store the ID
              testID="retry-delete-button"
            >
              <Text style={styles.retryText}>Dismiss</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Confirmation Dialog */}
      <LHConfirmationDialog
        visible={showConfirmDialog}
        message="You have unsaved changes. Are you sure you want to return to the NHO Dashboard? Your changes will be lost."
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
    padding: Platform.OS === "web" ? 20 : 16,
    
  },
  headerContainer: {
    width: "100%",
    paddingBottom: 16,
    backgroundColor: "transparent",
  },
  title: {
    fontSize: 28,
    fontWeight: "700",
    color: "#2D2D2D",
    lineHeight: 40,
    marginTop: 8,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
    backgroundColor: "#e0e0e0",
  },
  backButtonText: {
    fontSize: 16,
    color: "#333333",
  },
  contentContainer: {
    //padding: 20,
    //backgroundColor: "#ffffff",
    marginBottom: 16,
    borderRadius: 8,
    //shadowColor: "#000",
    //shadowOffset: { width: 0, height: 2 },
    //shadowOpacity: 0.1,
    //shadowRadius: 4,
    //elevation: 2,
    alignItems: "center",
    minHeight: 200,
  },
  titleText: {
    fontSize: Platform.OS === "web" || isIPad ? 26 : 18,
    fontWeight: "700",
    color: "#2D2D2D",
    marginBottom: 16,
    alignSelf: "flex-start",
  },
  errorContainer: {
    backgroundColor: "#f8d7da",
    padding: 12,
    borderRadius: 4,
    marginVertical: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    flex: 1,
  },
  retryButton: {
    backgroundColor: "#dc3545",
    padding: 8,
    borderRadius: 4,
    marginLeft: 8,
  },
  retryText: {
    color: "white",
    fontWeight: "500",
  },
  successContainer: {
    backgroundColor: "#d4edda",
    padding: 12,
    borderRadius: 4,
    marginVertical: 10,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  successText: {
    color: "#155724",
    fontSize: 14,
  },
  loadingContainer: {
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  tableContainer: {
    width: "100%",
    marginBottom: 16,
  },
  tableHeader: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
    color: "#2D2D2D",
  },
  tableDescription: {
    fontSize: 14,
    color: "#616161",
    marginBottom: 12,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    width: "100%",
    gap: 8,
    marginTop: 16,
  },
  actionButton: {
    width: Platform.OS === "web" ? "auto" : 160,
    minWidth: 160,
  },
});

export default AdminPage;
